import * as _ from 'underscore';

import { IOrderClause } from '../ServerApi/types/IOrderClause';

interface IComparableValueTransforms {
  [fieldName: string]: Function;
}

// Sorts things locally on the browser client (as opposed to sorting on the server)
export class LocalSorter<T> {

  private comparableValueTransforms: IComparableValueTransforms;

  constructor(){
    this.comparableValueTransforms = {};
  }

  // For a given 'fieldName', mutate the values under comparison using 'subFunc'.
  // Useful when we do not want to sort by the raw value, but by some transformed
  //   version of it.
  public addTransformForValueComparison(fieldName: string, transformFunc: Function) {
    // _.memoize creates a caching version of the function passed to it
    this.comparableValueTransforms[fieldName] = _.memoize(transformFunc);
  }

  // Sort 'unsortedItems' as specified by 'clauses'
  public sortByClauses<T>(unsortedItems: T[], clauses: IOrderClause[]): T[] {
    if (!unsortedItems || unsortedItems.length === 0) return unsortedItems;

    const itemsToSort = unsortedItems.slice(0);
    if (!clauses || clauses.length === 0) return itemsToSort;

    itemsToSort.sort((a: T, b: T) => this.compareByClauses(a, b, clauses, 0));
    return itemsToSort;
  }

  // Compare two items in the order specified by 'clauses'
  private compareByClauses<T>(a: T, b: T, clauses: IOrderClause[], clauseIndex: number): number {
    if (clauseIndex >= clauses.length) return 0;

    // Compare items according to the first clause
    const clause = clauses[clauseIndex];
    const fieldName = clause.field;
    let valA = (<any>a)[fieldName];
    let valB = (<any>b)[fieldName];

    // Maybe transform raw values before comparison
    const transformFunc = this.comparableValueTransforms[fieldName];
    if (transformFunc) {
      valA = transformFunc(valA);
      valB = transformFunc(valB);
    }

    if (valA > valB) {
      return clause.ascending? 1: -1;
    } else if (valA < valB) {
      return clause.ascending? -1: 1;
    } else {
      // terms are equal, compare items according to the next clause
      return this.compareByClauses(a, b, clauses, clauseIndex + 1);
    }
  }

}
